import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { vars } from '../../../reactive';
import { Voucher } from '../../../reactive/main';
import Colors from '../../../utils/Colors';
import debounce from '../../../utils/debounce';
import { GetVoucherByCode } from '../../../utils/queries';
import { FormTextInput } from '../../FormText/styled';
import { ErrorMessage } from '../../Shared/Shared';
import { ClearButton, Description, DiscountInputContainer, DiscountInputWrapper, Title } from './styled';

const VoucherInput = () => {
  const formOptions = useForm({
    defaultValues: {
      code: ''
    }
  });
  const {
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    register,
    watch
  } = formOptions;
  const [getVoucher, { data, loading: loadingVouchers, error: errorVouchers }] = useLazyQuery<{
    getVoucherByCode: Voucher;
  }>(GetVoucherByCode, { fetchPolicy: 'network-only' });
  const [debouncedLoading, setDebouncedLoading] = useState(false);
  const { t } = useTranslation();
  const loading = loadingVouchers || debouncedLoading;
  const voucher = data?.getVoucherByCode;

  const orderSummary = useReactiveVar(vars.orderSummary);
  const selectedProduct = useReactiveVar(vars.selectedProduct);
  const selectedBranch = vars.currentBranch();
  const selectedPets = vars.selectedPets();
  const promoCodePlaceholder = t('propCode.placeholder');

  const watchedCode = watch('code');

  const onSubmit = handleSubmit(form => {
    setDebouncedLoading(true);
    debounce(async () => {
      await getVoucher({
        variables: {
          code: form.code,
          BranchId: selectedBranch?.id,
          petsCount: selectedPets?.length,
          itemsTotal: Number(orderSummary?.total),
          ProductId: [selectedProduct?.id]
        }
      });
      setDebouncedLoading(false);
    }, 1000);
  });

  const onClear = () => {
    vars.vouchers(null);
    reset({ code: '' });
  };

  const submitVoucher = () => {
    if (!watchedCode) {
      onClear();
      return;
    }
    onSubmit();
  };

  useEffect(() => {
    submitVoucher();
  }, [watchedCode]);

  useEffect(() => {
    vars.vouchers(null);
  }, []);

  const isInvalid = !!watchedCode && !!data && !voucher && !loading;
  const error = isInvalid || !!errors.code || !!errorVouchers?.message;

  useEffect(() => {
    if (!error && voucher?.id) {
      vars.vouchers([voucher]);
    }
    if (error) {
      vars.vouchers(null);
    }
  }, [data, error]);

  return (
    <DiscountInputWrapper>
      <Title>{t('promoCode')}</Title>
      <DiscountInputContainer>
        <FormTextInput {...register('code')} placeholder={promoCodePlaceholder} noMargin width={100} error={error} noBorder />

        {loading && !!watchedCode && (
          <Description bold size={14}>
            Checking...
          </Description>
        )}
        {!loading && (
          <>
            {error && isInvalid && <ErrorMessage noMargin>Invalid Promo Code</ErrorMessage>}

            {!error && voucher?.id && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ color: Colors.black, fontSize: 12 }}>{'Valid'}</span>
              </div>
            )}
          </>
        )}
        {!!watchedCode && (
          <ClearButton onClick={onClear} disabled={loading} type="button">
            <span>Clear</span>
          </ClearButton>
        )}
      </DiscountInputContainer>
    </DiscountInputWrapper>
  );
};

export default VoucherInput;
